import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DxDropDownBoxComponent, DxFormComponent, DxTreeViewComponent } from 'devextreme-angular';
import { ToastrService } from 'ngx-toastr';
import { filter, forkJoin, map, of, switchMap, tap } from 'rxjs';
import { TrainingCategoryCreationAndUpdateModel } from 'src/app/models/training-category/training-category-creation-and-update.model';
import { TrainingCategoryFullModel } from 'src/app/models/training-category/training-category.model';
import { LoadingService } from 'src/app/services/loading.service';
import { TrainingCategoryService } from 'src/app/services/training-categories/training-category.service';
import { FileUtilService } from 'src/app/services/file-util.service';
import { ConfirmationDialogData } from 'src/app/components/shared/confirmation-dialog/confirmation-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { TextTemplateTypeModel } from 'src/app/models/text-templates/text-template-type.model';
import { UserContextService } from 'src/app/services/user-context.service';
import { TrainingGroupService } from 'src/app/services/training-group.service';
import { StandardTrainingCategoryService } from 'src/app/services/training-categories/standard-training-category.service';
import { TrainingGroupModel } from 'src/app/models/training-group.model';
import { StandardTrainingCategoryViewModel } from 'src/app/models/standard-training-category/standard-training-category-view.model';
import { DomSanitizer } from '@angular/platform-browser';
import { lockIcon, standardCategoryIcon } from 'src/app/app-icons';
import { StandardTrainingCategoryTypeModel } from 'src/app/models/standard-training-category/standard-training-category-type.model';
import { PageStateService } from 'src/app/services/page-state.service';
import { PageStateFlags } from 'src/app/constants/page-state.constants';

@Component({
  selector: 'app-training-categories-details',
  templateUrl: './training-categories-details.component.html',
  styleUrl: './training-categories-details.component.scss',
  standalone: false
})
export class TrainingCategoriesDetailsComponent implements OnInit {
  formData: TrainingCategoryCreationAndUpdateModel = {
    name: '',
    description: '',
    descriptionExtended: '',
    image: '',
    includeInLearningMatrix: true,
    standardTrainingCategoryId: null,
    associatedStandardTrainingCategoryId: null,
    associatedTrainingGroupIds: []
  };

  currentTrainingCategory: TrainingCategoryFullModel | null = null;
  editingMode = false;
  selectedDocument = {
    name: '',
    dataBase64: '',
  };

  fileToRemoveId: string | null = null;
  showDeleteFileModal: boolean = false;
  modalConfirmationOptions: ConfirmationDialogData | null = null;

  @ViewChild('form', { static: true }) form!: DxFormComponent;

  @ViewChild('associatedStandardCategoryDropDown', { static: false })
  associatedStandardCategoryDropDown!: DxDropDownBoxComponent;

  @ViewChild('trainingGroupsTree') 
  trainingGroupsTree!: DxTreeViewComponent;

  descriptionTextTemplateType: TextTemplateTypeModel =
    TextTemplateTypeModel.ExtendedDescription;
  
  standardTrainingCategories: StandardTrainingCategoryViewModel[] = [];
  trainingGroups: TrainingGroupModel[] = [];

  showStandardCategoryModal = false;
  selectedStandardCategory: StandardTrainingCategoryViewModel | null = null;

  showAssociatedStandardCategoryModal = false;

  get hasAutomationConfiguration(): boolean {
    return !!(
      (this.formData.associatedStandardTrainingCategoryId ||
        this.formData.standardTrainingCategoryId) &&
      this.formData.associatedTrainingGroupIds?.length
    );
  }

  constructor(
    private activatedRoute: ActivatedRoute,
    private trainingCategoryService: TrainingCategoryService,
    private router: Router,
    private loadingService: LoadingService,
    private toastrService: ToastrService,
    private fileUtilService: FileUtilService,
    private translateService: TranslateService,
    public userContext: UserContextService,
    private standardTrainingCategoryService: StandardTrainingCategoryService,
    private trainingGroupService: TrainingGroupService,
    private sanitized: DomSanitizer,
    private pageStateService: PageStateService
  ) {}

  ngOnInit(): void {
    this.activatedRoute.params
      .pipe(
        map((params) => params['id']),
        filter((id) => !!id),
        tap((id) => {
          this.editingMode = id !== 'new';
        }),
        switchMap((id) =>
          this.loadingService.load(
            forkJoin({
              trainingCategory: this.editingMode
                ? this.trainingCategoryService.getTrainingCategoryFull(id)
                : of(null),
              standardTrainingCategories: 
                this.standardTrainingCategoryService.getAvailableStandardTrainingCategories(
                  this.editingMode ? id : null,
                  this.userContext.currentPersonCulture ?? 'en'
                ),
              trainingGroups: this.trainingGroupService.getAllTrainingGroups(),
            })
          )
        )
      )
      .subscribe(({ 
        trainingCategory, standardTrainingCategories, trainingGroups
      }) => {
        this.currentTrainingCategory = trainingCategory;
        this.trainingGroups = trainingGroups;

        this.standardTrainingCategories = standardTrainingCategories.map(x => ({
          id: x.id,
          name: x.name ?? '',
          description: x.description ?? '',
          descriptionExtended: x.descriptionExtended ?? '',
          type: x.type,
          parentId: x.parentId,
          imageFile: x.imageFile
        }));

        if (!trainingCategory) return;

        const imageFileUrl = trainingCategory.trainingCategory.imageFile?.fileUrl;

        if (imageFileUrl) {
          this.loadingService
            .load(this.fileUtilService.avatarUrlToBase64(imageFileUrl))
            .subscribe((base64) => {
              const mimeType = this.fileUtilService.getImageMimeType(imageFileUrl);
              this.formData.image = `data:${mimeType};base64,${base64}`;
            });
        }

        this.formData.name = trainingCategory.trainingCategory.name;
        this.formData.description = trainingCategory.trainingCategory.description || '';
        this.formData.descriptionExtended =
          trainingCategory.trainingCategory.descriptionExtended || '';
        this.formData.includeInLearningMatrix = 
          trainingCategory.trainingCategory.includeInLearningMatrix;
        this.formData.standardTrainingCategoryId = 
          trainingCategory.trainingCategory.standardTrainingCategoryId;
        this.formData.associatedStandardTrainingCategoryId = 
          trainingCategory.trainingCategory.associatedStandardTrainingCategoryId;
        this.formData.associatedTrainingGroupIds = 
          trainingCategory.associatedTrainingGroups.map(g => g.id);

        this.setTreeViewValues();
      });
  }

  getStandardCategoryIcon() {
    return this.sanitized.bypassSecurityTrustHtml(standardCategoryIcon);
  }

  getLockIcon() {
    return this.sanitized.bypassSecurityTrustHtml(lockIcon);
  }
  
  private translate(key?: string): string {
    if (!key) {
      return '';
    }

    const value = this.translateService.instant(key);
    return value === key ? '' : value;
  }

  navigateBack(): void {
    this.pageStateService.setFlag(
      PageStateFlags.RestoreTrainingCategoriesOverview
    );

    this.router.navigate(['administration'], {
      queryParams: { tab: 'training-categories' },
    });
  }

  setTreeViewValues() {
    if(this.formData.standardTrainingCategoryId) {
      this.selectedStandardCategory = this.standardTrainingCategories
        .find(x => x.id == this.formData.standardTrainingCategoryId) ?? null;
    }

    this.trainingGroups.forEach((trainingGroup) => {
      trainingGroup.selected = this.formData.associatedTrainingGroupIds.includes(
        trainingGroup.id
      );
    });
  }

  submit(): void {
    const formValid = this.form.instance.validate().isValid;
    if (!formValid) {
      return;
    }

    const model: TrainingCategoryCreationAndUpdateModel = {
      ...this.formData,
      image: this.formData.image ? this.formData.image!.split(',')[1] : '',
    };

    if (this.editingMode && this.currentTrainingCategory) {
      const obs = this.trainingCategoryService.updateTrainingCategory(
        this.currentTrainingCategory.trainingCategory.id,
        model
      );
      this.loadingService.load(obs).subscribe(() => {
        this.toastrService.success('Training category saved');
      });
    } else {
      const obs = this.trainingCategoryService.addTrainingCategory(model);
      this.loadingService.load(obs).subscribe((createdCategory) => {
        this.toastrService.success('Training category added');

        this.router.navigate([
          'administration',
          'training-categories',
          createdCategory.trainingCategory.id,
        ]);
      });
    }
  }

  onTrainingGroupSelected(e: any) {
    if (e.node.selected) {
      this.formData.associatedTrainingGroupIds = [
        ...this.formData.associatedTrainingGroupIds,
        e.node.key,
      ];
    } else {
      this.formData.associatedTrainingGroupIds =
        this.formData.associatedTrainingGroupIds.filter((id) => id !== e.node.key);
    }
  }

  onCategoryImageSelected(files: any) {
    const file = files[0];
    const reader = new FileReader();
    reader.onload = () => {
      this.formData!.image = reader!.result!.toString();
    };
    reader.readAsDataURL(file);
  }

  onDocumentSelected(files: any) {
    const file = files[0];
    const reader = new FileReader();
    reader.onload = () => {
      this.selectedDocument.name = file.name;
      this.selectedDocument.dataBase64 = reader!
        .result!.toString()
        .split(',')[1];

      this.loadingService
        .load(
          this.trainingCategoryService.addTrainingCategoryFile(
            this.currentTrainingCategory!.trainingCategory!.id,
            this.selectedDocument
          )
        )
        .subscribe((file) => {
          this.selectedDocument.name = '';
          this.selectedDocument.dataBase64 = '';
          this.currentTrainingCategory?.files.push(file);
        });
    };
    reader.readAsDataURL(file);
  }

  onRemoveFileClicked(file: any) {
    this.showDeleteFileModal = true;
    this.fileToRemoveId = file.fileId;

    this.modalConfirmationOptions = {
      hideUndo: true,
      submitButtonText: this.translateService.instant('COMMON.YES'),
      message: this.translateService.instant(
        'ADMINISTRATION.DELETE-CATEGORY-FILE.TITLE'
      ),
      dismissButtonText: this.translateService.instant('COMMON.NO'),
    };
  }

  onRemoveFileConfirm() {
    this.loadingService
      .load(
        this.trainingCategoryService.deleteTrainingCategoryFile(
          this.currentTrainingCategory!.trainingCategory!.id,
          this.fileToRemoveId!
        )
      )
      .subscribe(() => {
        this.showDeleteFileModal = false;
        this.currentTrainingCategory!.files =
          this.currentTrainingCategory!.files.filter(
            (f) => f.fileId !== this.fileToRemoveId!
          );
      });
  }

  onTemplateContentChanged(content: string) {
    this.formData.descriptionExtended = content;
  }

  openAssociatedStandardCategoryModal(): void {
    this.associatedStandardCategoryDropDown.instance.close();
    this.showAssociatedStandardCategoryModal = true;
  }

  clearAssociatedStandardCategory(e: any): void {
    if (e.value) return;
    this.formData.associatedStandardTrainingCategoryId = null;
  }

  onAssociatedStandardCategorySelected(
    category: StandardTrainingCategoryViewModel
  ): void {
    this.formData.associatedStandardTrainingCategoryId = category.id;
  }

  getStandardCategoryPath(category: StandardTrainingCategoryViewModel): string {
    const parts: string[] = [];

    const type =
      category.type === StandardTrainingCategoryTypeModel.PrivateInsurance
        ? this.translateService.instant('STANDARD-CATEGORIES.STANDARD-CATEGORY-TYPES.PRIVATE-INSURANCES')
        : this.translateService.instant('STANDARD-CATEGORIES.STANDARD-CATEGORY-TYPES.CORPORATE-INSURANCES');

    parts.push(type);

    if (category.parentId) {
      const parent = this.standardTrainingCategories.find(x => x.id === category.parentId);

      if (parent) {
        parts.push(parent.name);
      }
    }

    return parts.join(' → ');
  }

  openStandardCategoryModal(): void {
    this.showStandardCategoryModal = true;
  }

  clearStandardCategory(e: any): void {
    if (e.value) return;
    e.event?.stopPropagation();

    this.formData.standardTrainingCategoryId = null;
    this.selectedStandardCategory = null;
  }

  onStandardCategorySelected(
    category: StandardTrainingCategoryViewModel
  ): void {
    this.selectedStandardCategory = category;
    this.formData.standardTrainingCategoryId = category.id;
    this.formData.associatedStandardTrainingCategoryId = null;

    this.formData.name = category.name;
    this.formData.description = category.description;
    this.formData.descriptionExtended = category.descriptionExtended;

    const imageUrl = category.imageFile?.fileUrl;

    if (imageUrl) {
      this.loadingService
        .load(
          this.fileUtilService.avatarUrlToBase64(imageUrl)
        )
        .subscribe((base64) => {
          const mimeType = this.fileUtilService.getImageMimeType(imageUrl);
          this.formData.image = `data:${mimeType};base64,${base64}`;
        });
    }
  }

  onTrainingGroupsValueChanged(e: any): void {
    if (e.value === null) {
      this.formData.associatedTrainingGroupIds = [];
      this.trainingGroupsTree?.instance?.unselectAll();

      this.setTreeViewValues();
    }
  }
}
